import { useMemo, useState } from "react";
import { Checkbox, SearchBar } from "@legal-500/legal500-design-system";
import {
  GLOBAL_SCOPE_ID,
  PUBLICATION_REGIONS,
  getCountryIdsForScope,
  getCountryName,
  getRegionsForScope,
  type PublicationRegion,
} from "@legal-500/discovery/data";

const GLOBAL_LABEL = "Global (all regions)";

const SELECTABLE_REGIONS = PUBLICATION_REGIONS.filter(
  region => region.entitlementScopeId !== null || region.publicationIds.length > 0,
);

/**
 * "Worldwide" / site-structure id "5693884" is a distinct country-peer ranking entity, not the
 * Global apex — if it ever shows up as a raw firm-entitlement scope id, getRegionsForScope would
 * treat it as its own worldwide sentinel and over-expand to every region (including the internal
 * "*-guide" ones). Strip it before resolving so it can never smuggle in a false-bounded tree.
 */
const NON_SELECTABLE_RAW_SCOPE_IDS = new Set(["Worldwide", "5693884"]);

export type ScopeTreePickerProps = {
  /** The firm's entitled scope ids for this product, or null when unknown/unbounded. */
  firmScopeIds: string[] | null;
  value: string[];
  onChange: (scopeIds: string[]) => void;
  disabled?: boolean;
};

function toggleScope(currentScopeIds: string[], scopeId: string): string[] {
  return currentScopeIds.includes(scopeId)
    ? currentScopeIds.filter(existingScopeId => existingScopeId !== scopeId)
    : [...currentScopeIds, scopeId];
}

function resolveVisibleRegions(firmScopeIds: string[] | null): PublicationRegion[] {
  if (firmScopeIds === null || firmScopeIds.length === 0) return SELECTABLE_REGIONS;
  const safeScopeIds = firmScopeIds.filter(scopeId => !NON_SELECTABLE_RAW_SCOPE_IDS.has(scopeId));
  return getRegionsForScope(safeScopeIds).filter(
    region => region.entitlementScopeId !== null || region.publicationIds.length > 0,
  );
}

function regionScopeId(region: PublicationRegion): string {
  return region.entitlementScopeId ?? region.memberCountryIds[0] ?? region.slug;
}

function regionCountryIds(region: PublicationRegion): string[] {
  if (region.entitlementScopeId) return getCountryIdsForScope(region.entitlementScopeId);
  return [...region.memberCountryIds];
}

function regionMatchesSearch(region: PublicationRegion, normalizedSearch: string): boolean {
  if (region.name.toLowerCase().includes(normalizedSearch)) return true;
  return regionCountryIds(region).some(countryId =>
    (getCountryName(countryId) ?? "").toLowerCase().includes(normalizedSearch),
  );
}

function countriesForDisplay(region: PublicationRegion, normalizedSearch: string): string[] {
  const countryIds = regionCountryIds(region);
  if (normalizedSearch === "" || region.name.toLowerCase().includes(normalizedSearch)) {
    return countryIds;
  }
  return countryIds.filter(countryId =>
    (getCountryName(countryId) ?? "").toLowerCase().includes(normalizedSearch),
  );
}

export function ScopeTreePicker({ firmScopeIds, value, onChange, disabled }: ScopeTreePickerProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [manuallyExpandedSlugs, setManuallyExpandedSlugs] = useState<Set<string>>(new Set());

  const isBounded = firmScopeIds !== null && firmScopeIds.length > 0;
  const visibleRegions = useMemo(() => resolveVisibleRegions(firmScopeIds), [firmScopeIds]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const globalVisible =
    normalizedSearch === "" || GLOBAL_LABEL.toLowerCase().includes(normalizedSearch);
  const filteredRegions = visibleRegions.filter(
    region => normalizedSearch === "" || regionMatchesSearch(region, normalizedSearch),
  );

  function isRegionExpanded(region: PublicationRegion): boolean {
    if (normalizedSearch !== "" && regionMatchesSearch(region, normalizedSearch)) return true;
    return manuallyExpandedSlugs.has(region.slug);
  }

  function toggleExpanded(regionSlug: string) {
    setManuallyExpandedSlugs(previous => {
      const next = new Set(previous);
      if (next.has(regionSlug)) next.delete(regionSlug);
      else next.add(regionSlug);
      return next;
    });
  }

  return (
    <div>
      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        onClear={() => setSearchTerm("")}
        placeholder="Search countries or regions…"
      />

      {!isBounded && (
        <p
          style={{
            fontSize: "var(--font-size-sm)",
            color: "var(--color-text-secondary)",
            marginTop: "0.5rem",
          }}
        >
          Firm entitlement unknown — full list shown.
        </p>
      )}

      <div
        style={{
          marginTop: "0.75rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.375rem",
          maxHeight: "20rem",
          overflowY: "auto",
        }}
      >
        {globalVisible && (
          <label>
            <Checkbox
              checked={value.includes(GLOBAL_SCOPE_ID)}
              onChange={() => onChange(toggleScope(value, GLOBAL_SCOPE_ID))}
              label={GLOBAL_LABEL}
              aria-label={GLOBAL_LABEL}
              disabled={disabled}
            />
          </label>
        )}

        {filteredRegions.map(region => {
          const scopeId = regionScopeId(region);
          const expanded = isRegionExpanded(region);
          const countryIds = countriesForDisplay(region, normalizedSearch);

          return (
            <div key={region.slug}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                <button
                  type="button"
                  onClick={() => toggleExpanded(region.slug)}
                  aria-expanded={expanded}
                  aria-label={expanded ? `Collapse ${region.name}` : `Expand ${region.name}`}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0.125rem",
                    color: "var(--color-text-secondary)",
                  }}
                >
                  {expanded ? "▾" : "▸"}
                </button>
                <label style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={value.includes(scopeId)}
                    onChange={() => onChange(toggleScope(value, scopeId))}
                    label={region.name}
                    aria-label={region.name}
                    disabled={disabled}
                  />
                </label>
              </div>
              {expanded && (
                <div
                  style={{
                    marginLeft: "1.75rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.25rem",
                    marginTop: "0.25rem",
                  }}
                >
                  {countryIds.map(countryId => (
                    <label key={countryId}>
                      <Checkbox
                        checked={value.includes(countryId)}
                        onChange={() => onChange(toggleScope(value, countryId))}
                        label={getCountryName(countryId) ?? countryId}
                        aria-label={getCountryName(countryId) ?? countryId}
                        disabled={disabled}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p
        style={{
          marginTop: "0.5rem",
          fontSize: "var(--font-size-sm)",
          color: "var(--color-text-secondary)",
        }}
      >
        {value.length === 0
          ? "No countries/regions selected — unrestricted (firm-wide)."
          : `${value.length} selected.`}
      </p>
    </div>
  );
}

export default ScopeTreePicker;
